import { ThemeEvents } from '@theme/events'
import { Component } from '@theme/component'

/**
 * Displays a pre-order message when the selected variant has pre-order info.
 * Listens for variant changes via delegated 'change' and variant:update.
 */
class PreOrderMessage extends Component {
  #abortController = new AbortController()

  connectedCallback() {
    super.connectedCallback()
    const section = this.#getSection()
    if (!section) return

    const { signal } = this.#abortController
    section.addEventListener('change', this.#handleChangeDelegated.bind(this), { signal })
    section.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate.bind(this), { signal })
    this.#checkInitialVariant()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.#abortController.abort()
  }

  #getSection() {
    return (
      this.closest('.shopify-section') ||
      this.closest('.product-information') ||
      this.closest('dialog') ||
      this.closest('product-card')
    )
  }

  #getVariantPicker() {
    const section = this.#getSection()
    if (!section || !this.dataset.productId) return null
    const productId = String(this.dataset.productId)
    const pickers = section.querySelectorAll('variant-picker')
    for (const picker of pickers) {
      if (String(picker.dataset.productId || '') === productId) return picker
    }
    return null
  }

  #handleChangeDelegated(event) {
    if (!(event.target instanceof HTMLElement)) return
    const variantPicker = event.target.closest('variant-picker')
    if (!variantPicker || String(variantPicker.dataset.productId || '') !== String(this.dataset.productId || ''))
      return
    this.#handleVariantChange(event)
  }

  #handleVariantChange(event) {
    const target = event.target
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      this.#updateMessage(target)
      return
    }
    if (target instanceof HTMLSelectElement) {
      const option = target.options[target.selectedIndex]
      if (option && option.dataset.variantId) {
        this.#updateMessageFromVariantId(option.dataset.variantId, option.dataset.preorderDesc)
      }
    }
  }

  #updateMessageFromVariantId(variantId, preorderDesc) {
    if (preorderDesc?.trim()) this.#showMessage(preorderDesc)
    else this.#fetchVariantMetafields(variantId)
  }

  #checkInitialVariant() {
    if (this.refs.preOrderText?.textContent?.trim()) {
      this.classList.remove('hidden')
      return
    }
    const variantPicker = this.#getVariantPicker()
    const selectedInput = variantPicker?.querySelector('input[type="radio"]:checked')
    if (selectedInput) this.#updateMessage(selectedInput)
  }

  #handleVariantUpdate(event) {
    if (String(event.detail.data.productId) !== String(this.dataset.productId)) return
    const variant = event.detail.resource

    if (variant?.metafields?.order?.pre_order) {
      this.#showMessage(variant.metafields.order.pre_order)
      return
    }

    const variantPicker = this.#getVariantPicker()
    const selectedInput = variantPicker?.querySelector('input[type="radio"]:checked')
    if (selectedInput) {
      this.#updateMessage(selectedInput)
      return
    }

    if (variant?.id) {
      this.#fetchVariantMetafields(variant.id)
    } else {
      this.#hideMessage()
    }
  }

  #updateMessage(input) {
    const desc = input.dataset.preorderDesc?.trim()
    if (desc) {
      this.#showMessage(desc)
    } else if (input.dataset.variantId) {
      this.#fetchVariantMetafields(input.dataset.variantId)
    } else {
      this.#hideMessage()
    }
  }

  async #fetchVariantMetafields(variantId) {
    try {
      const productForm = this.closest('product-form-component')
      if (!productForm) return

      const productId = productForm.dataset.productId
      const productUrl = productForm.dataset.productUrl
      if (!productId || !productUrl) return

      const url = new URL(productUrl, window.location.origin)
      url.searchParams.set('variant', variantId)
      url.searchParams.set('section_id', this.dataset.sectionId)

      const response = await fetch(url.href, {
        headers: { Accept: 'text/html' },
      })

      if (!response.ok) {
        this.#hideMessage()
        return
      }

      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const variantPicker = doc.querySelector('variant-picker')
      const variantJson = variantPicker?.querySelector('script[type="application/json"]')?.textContent

      if (variantJson) {
        const variant = JSON.parse(variantJson)
        if (variant?.metafields?.order?.pre_order) {
          this.#showMessage(variant.metafields.order.pre_order)
          return
        }
      }
      const input = doc.querySelector('input[type="radio"][data-variant-id="' + variantId + '"]')
      if (input?.dataset.preorderDesc) this.#showMessage(input.dataset.preorderDesc)
      else this.#hideMessage()
    } catch (e) {
      this.#hideMessage()
    }
  }

  #showMessage(description) {
    if (this.refs.preOrderText) {
      this.refs.preOrderText.textContent = description
      this.classList.remove('hidden')
    }
  }

  #hideMessage() {
    this.classList.add('hidden')
  }
}

if (!customElements.get('pre-order-message')) {
  customElements.define('pre-order-message', PreOrderMessage)
}
